/**
 * BLInstructionsRequestModal
 * ────────────────────────────────────────────────────────────────
 * Send & Track modal used by the "Request BL Instructions" button
 * on PositionFoldersPage. Replaces the old silent flag-only flow.
 *
 * • To       — defaults to PF.customer_email
 * • CC       — chips, prefilled with salesperson + ops inbox
 * • BCC      — chips, empty by default
 * • Subject  — template, editable
 * • Body     — HTML template with the secure link injected. The
 *              link itself is shown as a read-only "Public link"
 *              preview at the top so the operator can copy it.
 *
 * On submit → POST /api/bl-instructions/{pf_id}/send-email
 *   The backend mints (or reuses) a tokenized public link, sends
 *   the email via Graph→SMTP fallback, flips PF status to
 *   "requested", stores recipients and starts the 48h auto-nudge
 *   loop.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { X, Send, Mail, Copy, Loader2, Plus } from "lucide-react";

const OPS_FALLBACK = "operations@europacific.com";

function pickSalespersonEmail(folder) {
  return (
    folder?.salesperson_email ||
    folder?.sales_email ||
    folder?.assignee_email ||
    folder?.assigned_to_email ||
    ""
  );
}

function buildDefaultSubject(folder) {
  const pf = folder?.pf_number || folder?.job_number || "shipment";
  return `Action needed — BL Instructions for ${pf}`;
}

function buildDefaultBody(folder, contactName) {
  const pf = folder?.pf_number || folder?.job_number || "your shipment";
  const pol = folder?.pol || folder?.port_of_loading || folder?.loading_port || "";
  const pod = folder?.pod || folder?.port_of_discharge || folder?.discharge_port || "";
  const greeting = contactName ? `Dear ${contactName},` : "Hello,";
  return (
    `${greeting}\n\n` +
    `To complete the booking and produce your Bill of Lading, we need your ` +
    `BL instructions (shipper, consignee, notify party, marks & numbers, ` +
    `cargo description).\n\n` +
    `Please use the secure link in the email below — no login required.\n\n` +
    `Shipment: ${pf}\n` +
    (pol || pod ? `Route: ${pol} → ${pod}\n` : "") +
    `\nWhat we need:\n` +
    `  • HBL only vs MBL + HBL\n` +
    `  • Full shipper / consignee / notify-party details\n` +
    `  • Cargo description, marks & numbers, gross weight, volume\n` +
    `  • Special instructions (Telex release, freight prepaid/collect, …)\n\n` +
    `Reply to this email if you need help.\n\n` +
    `— myEuropacific Operations`
  );
}

export default function BLInstructionsRequestModal({ folder, onClose, onSent }) {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [to, setTo] = useState(folder?.customer_email || "");
  const [ccList, setCcList] = useState(() => {
    const out = [];
    const sp = pickSalespersonEmail(folder);
    if (sp) out.push(sp);
    if (!out.includes(OPS_FALLBACK)) out.push(OPS_FALLBACK);
    return out;
  });
  const [bccList, setBccList] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [subject, setSubject] = useState(buildDefaultSubject(folder));
  const [bodyText, setBodyText] = useState(buildDefaultBody(folder, folder?.contact_name));
  const [useDefaultTemplate, setUseDefaultTemplate] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // Re-seed if folder changes (rare in practice)
    setTo(folder?.customer_email || "");
    setSubject(buildDefaultSubject(folder));
    setBodyText(buildDefaultBody(folder, folder?.contact_name));
  }, [folder?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addChip = (raw, list, setList, clearInput) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (!v.includes("@")) {
      toast.error("Not a valid email");
      return;
    }
    if (list.some(x => x.toLowerCase() === v.toLowerCase())) {
      clearInput();
      return;
    }
    setList([...list, v]);
    clearInput();
  };

  const removeChip = (idx, list, setList) => {
    setList(list.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (!to || !to.includes("@")) {
      toast.error("Please enter a valid recipient email in the To field");
      return;
    }
    setSending(true);
    try {
      // If operator edited the default body, we pass body_html=null to let
      // the backend render its rich HTML template (with the link button).
      // Only override when they explicitly toggle template off.
      const payload = {
        to,
        cc: ccList,
        bcc: bccList,
        subject: subject.trim() || undefined,
        body_html: useDefaultTemplate
          ? undefined
          : `<html><body style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1f2937;line-height:1.55;white-space:pre-wrap;">${bodyText
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")}</body></html>`,
      };
      const res = await axios.post(
        `${API_URL}/bl-instructions/${folder.id}/send-email`,
        payload,
        { headers }
      );
      toast.success(`BL Instructions email sent to ${res.data.sent_to}`);
      if (onSent) onSent(res.data);
      if (onClose) onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Send failed: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const copyLinkPreview = () => {
    // The actual link is minted server-side — we show a placeholder so the
    // operator knows a link WILL be injected. The real link comes back in
    // the response after sending.
    toast.info("The secure link will be generated and inserted by the backend when you send.");
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      data-testid="bl-instructions-request-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose?.(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Send BL Instructions Request</h2>
              <p className="text-xs text-slate-500">
                {folder?.pf_number || folder?.job_number} · secure link valid for 24 days
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="p-2 rounded hover:bg-slate-100 disabled:opacity-50"
            data-testid="bl-request-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* To */}
          <div>
            <Label className="text-xs font-medium">To</Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              data-testid="bl-request-to"
            />
            {!folder?.customer_email && (
              <p className="text-[11px] text-amber-600 mt-1">
                No customer_email stored on this PF — please enter one above.
              </p>
            )}
          </div>

          {/* CC */}
          <div>
            <Label className="text-xs font-medium">CC</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {ccList.map((c, i) => (
                <span
                  key={c + i}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs"
                  data-testid={`bl-cc-chip-${i}`}
                >
                  {c}
                  <button
                    onClick={() => removeChip(i, ccList, setCcList)}
                    className="hover:text-blue-950"
                    aria-label={`Remove ${c}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addChip(ccInput, ccList, setCcList, () => setCcInput(""));
                  }
                }}
                placeholder="add cc email and press Enter"
                data-testid="bl-cc-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addChip(ccInput, ccList, setCcList, () => setCcInput(""))}
                data-testid="bl-cc-add"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* BCC */}
          <div>
            <Label className="text-xs font-medium">BCC</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {bccList.map((c, i) => (
                <span
                  key={c + i}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-800 text-xs"
                  data-testid={`bl-bcc-chip-${i}`}
                >
                  {c}
                  <button
                    onClick={() => removeChip(i, bccList, setBccList)}
                    className="hover:text-slate-950"
                    aria-label={`Remove ${c}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={bccInput}
                onChange={(e) => setBccInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addChip(bccInput, bccList, setBccList, () => setBccInput(""));
                  }
                }}
                placeholder="add bcc email and press Enter"
                data-testid="bl-bcc-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addChip(bccInput, bccList, setBccList, () => setBccInput(""))}
                data-testid="bl-bcc-add"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs font-medium">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="bl-request-subject"
            />
          </div>

          {/* Body / template toggle */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-medium">Message</Label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useDefaultTemplate}
                  onChange={(e) => setUseDefaultTemplate(e.target.checked)}
                  data-testid="bl-use-default-template"
                />
                Use rich HTML template (with secure link button)
              </label>
            </div>
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={10}
              disabled={useDefaultTemplate}
              className="font-mono text-sm"
              data-testid="bl-request-body"
            />
            {useDefaultTemplate && (
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <Copy className="h-3 w-3" />
                A secure link will be auto-generated and injected into the email body.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t sticky bottom-0 bg-white">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            data-testid="bl-request-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !to}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bl-request-send"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1.5" />
                Send & Track
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
